import React from 'react';
import { cn } from './cn';

type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'link';
type ButtonSize = 'md' | 'sm' | 'lg';

export type ButtonProps = (React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement>) & {
    variant?: ButtonVariant;
    size?: ButtonSize;
    isLoading?: boolean;
    as?: 'button' | 'a';
  };

export const Button = React.forwardRef<HTMLElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, children, as = 'button', disabled, ...props }, ref) => {
    const base = 'ds-btn';
    const variantClass =
      variant === 'ghost'
        ? 'ds-btn--ghost'
        : variant === 'danger'
        ? 'ds-btn--danger'
        : variant === 'link'
        ? 'ds-btn--link'
        : 'ds-btn--primary';

    const sizeClass =
      size === 'sm' ? 'ds-btn--sm' : size === 'lg' ? 'ds-btn--lg' : 'ds-btn--md';

    const Comp: any = as === 'a' ? 'a' : 'button';
    const sharedProps = {
      className: cn(base, variantClass, sizeClass, className),
      'aria-busy': isLoading || undefined,
      ...props,
    };

    if (as === 'a') {
      return (
        <Comp {...sharedProps} aria-disabled={disabled || undefined} role="button" tabIndex={disabled ? -1 : undefined}>
          {isLoading ? <span className="sr-only">Loading</span> : null}
          {children}
        </Comp>
      );
    }

    return (
      <Comp ref={ref as React.Ref<HTMLButtonElement>} disabled={disabled} {...sharedProps}>
        {isLoading ? <span className="sr-only">Loading</span> : null}
        {children}
      </Comp>
    );
  }
);

Button.displayName = 'Button';
